import { PeriodEnum } from '@core/enums/period.enums'
import { PeriodMap, StreamEventTimeItemMap } from '@domain/match-events.models'
import { TeamType } from '@mediacoach/ui'

export interface TimelineEventPayload {
  eventType: string
  iconName: string
  label: string
  minute: string
  playerAlias: string
  playerPhotoUrl: string
  playerNumber: number
  timeItems: StreamEventTimeItemMap
  period?: string
  edge?: PeriodEdge
}

export interface PenaltyShot {
  id: number
  value: number
  status: 'scored' | 'missed'
  disabled: boolean
  segmentId: string
  eventPayload: TimelineEventPayload
}

export interface TeamPenaltyData {
  shots: PenaltyShot[]
  logo: string
}

export interface PenaltiesPayload {
  home: TeamPenaltyData
  away: TeamPenaltyData
}

export interface PeriodDefinition {
  id: keyof PeriodMap
  label?: string
  tooltip: string
  flex: number
  defaultStart?: number
  defaultEnd?: number
  alwaysShow?: boolean
  mobileGroupId?: string
}

export enum EventType {
  Shot = 'Shot',
  YellowCard = 'YellowCard',
  DoubleYellowCard = 'DoubleYellowCard',
  RedCard = 'RedCard',
  Substitution = 'Substitution',
  Goal = 'Goal',
  GoalAssist = 'GoalAssist',
  MissedPenal = 'MissedPenal',
  OwnGoal = 'OwnGoal',
  PlayerOff = 'PlayerOff',
  PlayerOn = 'PlayerOn',
  ShotPost = 'ShotPost',
  PlayerInjured = 'PlayerInjured',
  DisallowedGoal = 'DisallowedGoal',
  Penalty = 'Penalty',
  VarGoalAwardedSuccess = 'VAR_GoalAwarded_Success',
  VarCardUpgradeSuccess = 'VAR_CardUpgrade_Success',
  VarGoalNotAwardedSuccess = 'VAR_GoalNotAwarded_Success',
  VarMistakenIdentitySuccess = 'VAR_MistakenIdentity_Success',
  VarOtherSuccess = 'VAR_Other_Success',
  VarPenaltyAwardedSuccess = 'VAR_PenaltyAwarded_Success',
  VarPenaltyNotAwardedSuccess = 'VAR_PenaltyNotAwarded_Success',
  VarRedCardGivenSuccess = 'VAR_RedCardGiven_Success',
  VarGoalAwardedCancelled = 'VAR_GoalAwarded_Cancelled',
  VarCardUpgradeCancelled = 'VAR_CardUpgrade_Cancelled',
  VarGoalNotAwardedCancelled = 'VAR_GoalNotAwarded_Cancelled',
  VarMistakenIdentityCancelled = 'VAR_MistakenIdentity_Cancelled',
  VarOtherCancelled = 'VAR_Other_Cancelled',
  VarPenaltyAwardedCancelled = 'VAR_PenaltyAwarded_Cancelled',
  VarPenaltyNotAwardedCancelled = 'VAR_PenaltyNotAwarded_Cancelled',
  VarRedCardGivenCancelled = 'VAR_RedCardGiven_Cancelled'
}

export enum PeriodEdge {
  Start = 'start',
  End = 'end'
}

export enum PeriodGroup {
  Normal,
  Extra
}

export interface EventTranslations {
  [EventType.Shot]: string
  [EventType.YellowCard]: string
  [EventType.DoubleYellowCard]: string
  [EventType.RedCard]: string
  [EventType.Substitution]?: string
  [EventType.Goal]: string
  [EventType.GoalAssist]?: string
  [EventType.MissedPenal]: string
  [EventType.OwnGoal]: string
  [EventType.PlayerOff]?: string
  [EventType.PlayerOn]?: string
  [EventType.ShotPost]?: string
  [EventType.PlayerInjured]?: string
  [EventType.DisallowedGoal]?: string
  [EventType.Penalty]?: string
  [EventType.VarGoalAwardedSuccess]?: string
  [EventType.VarCardUpgradeSuccess]?: string
  [EventType.VarGoalNotAwardedSuccess]?: string
  [EventType.VarMistakenIdentitySuccess]?: string
  [EventType.VarOtherSuccess]?: string
  [EventType.VarPenaltyAwardedSuccess]?: string
  [EventType.VarPenaltyNotAwardedSuccess]?: string
  [EventType.VarRedCardGivenSuccess]?: string
  [EventType.VarGoalAwardedCancelled]?: string
  [EventType.VarCardUpgradeCancelled]?: string
  [EventType.VarGoalNotAwardedCancelled]?: string
  [EventType.VarMistakenIdentityCancelled]?: string
  [EventType.VarOtherCancelled]?: string
  [EventType.VarPenaltyAwardedCancelled]?: string
  [EventType.VarPenaltyNotAwardedCancelled]?: string
  [EventType.VarRedCardGivenCancelled]?: string
}

export interface PeriodTranslations {
  [PeriodEnum.First]: string
  [PeriodEnum.Second]: string
  [PeriodEnum.ExtraFirst]: string
  [PeriodEnum.ExtraSecond]: string
  [PeriodEnum.Penalties]: string
  [PeriodEnum.End]: string
}

export interface EventGroupData {
  team: TeamType
  period?: PeriodEnum
  groupIndex: number
}

export interface TimelineEvent {
  id: number
  label?: string
  eventType?: EventType
  period?: string
  minute?: number
  player?: string
  playerFirstname?: string
  playerSurname?: string
  playerAlias?: string
  playerPhotoUrl?: string
  timeInSeconds?: string
  position?: number
  rawPosition?: number
  fullPosition?: number
  minuteLabel?: string
  offMinuteLabel?: string
  disabled?: boolean
  isColliding?: boolean
  isGoal?: boolean
  isSelected?: boolean
  iconPath?: string
  penaltyIconPath?: string
  attributes?: { [key: string]: any }
}

export interface EventGroup {
  isSelected?: boolean
  position?: number
  rawPosition?: number
  events: TimelineEvent[]
}

export interface TeamEventGroupConfig {
  [PeriodEnum.First]: EventGroup[]
  [PeriodEnum.Second]: EventGroup[]
  [PeriodEnum.ExtraFirst]?: EventGroup[]
  [PeriodEnum.ExtraSecond]?: EventGroup[]
  [PeriodEnum.Penalties]?: EventGroup[]
}

export interface TeamConfig {
  [PeriodEnum.First]: TimelineEvent[]
  [PeriodEnum.Second]: TimelineEvent[]
  [PeriodEnum.ExtraFirst]?: TimelineEvent[]
  [PeriodEnum.ExtraSecond]?: TimelineEvent[]
  [PeriodEnum.Penalties]?: TimelineEvent[]
  logo: string
}

export interface PeriodItem {
  id: string
  start: number
  defaultEnd: number
  end: number
  label: string
}

export interface PeriodConfig {
  [PeriodEnum.First]: PeriodItem
  [PeriodEnum.Second]: PeriodItem
  [PeriodEnum.ExtraFirst]?: PeriodItem
  [PeriodEnum.ExtraSecond]?: PeriodItem
  [PeriodEnum.Penalties]?: PeriodItem
}

export interface TimelineConfig {
  [TeamType.Home]: TeamConfig
  [TeamType.Away]: TeamConfig
  periods?: PeriodConfig
  deletedEvents: TimelineConfig
}
